'use client'

import { HsTaxCBPGCalculatorResponse } from '@/modules/hs/types/hs-tax.response'
import '@/modules/hs/styles/hs-cbpg.css'
import { formatCurrency } from '@/modules/shared/utils/number.util'

interface HsTaxCBPGCalculatorResultProps {
  result: HsTaxCBPGCalculatorResponse
  quantity: number
  unitPrice: number
  currency: string
  exchangeRate?: number
}

export default function HsTaxCBPGCalculatorResult({
  result,
  quantity,
  unitPrice,
  currency,
  exchangeRate = 26000,
}: HsTaxCBPGCalculatorResultProps) {
  // Trường hợp không bị đánh thuế CBPG
  if (result.taxRate === null) {
    return (
      <div className="cbpg-no-tax-message">
        <p>
          <strong>Không áp dụng thuế chống bán phá giá</strong>
        </p>
      </div>
    )
  }

  // Lấy tỷ giá quy đổi sang VND
  const effectiveRate = currency === 'VND' ? 1 : exchangeRate

  // Tính tổng giá trị hàng hóa theo VND
  const totalValueVND = quantity * unitPrice * effectiveRate

  // Tính số thuế phải nộp = tổng giá trị * thuế suất (%)
  const totalTaxVND = totalValueVND * (result.taxRate / 100)

  // Format ngày tháng
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  return (
    <div className="cbpg-result-content">
      <h4 className="cbpg-result-title">Kết quả:</h4>

      <table className="cbpg-result-table">
        <tbody>
          {/* Mức thuế chống bán phá giá */}
          <tr>
            <td className="cbpg-result-label">Mức thuế chống bán phá giá:</td>
            <td className="cbpg-result-value cbpg-result-tax-rate">
              {result.taxRate.toLocaleString('vi-VN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              %
            </td>
          </tr>

          {/* Số thuế phải nộp */}
          <tr>
            <td className="cbpg-result-label">
              Số thuế chống bán phá giá phải nộp:
            </td>
            <td className="cbpg-result-value cbpg-result-total-tax">
              {formatCurrency(Math.round(totalTaxVND))} VND
            </td>
          </tr>

          {/* Thời gian áp thuế */}
          <tr>
            <td className="cbpg-result-label">Thời gian áp thuế:</td>
            <td className="cbpg-result-value">
              {result.effectiveFrom && result.effectiveTo
                ? `${formatDate(result.effectiveFrom)} - ${formatDate(result.effectiveTo)}`
                : 'N/A'}
            </td>
          </tr>

          {/* Xuất xứ */}
          <tr>
            <td className="cbpg-result-label">Xuất xứ:</td>
            <td className="cbpg-result-value">{result.countryName || 'N/A'}</td>
          </tr>
        </tbody>
      </table>

      <p className="cbpg-result-note">
        Lưu ý: Kết quả tính trên đây chỉ mang tính chất tham khảo và không phải
        là ý kiến tư vấn cho bất kỳ trường hợp cụ thể nào.
      </p>
    </div>
  )
}
